/* component-sticky-atc.js — <sticky-atc>
   Reveals a compact bottom bar (image + title + price + ATC) once the main
   buy box scrolls out of view. Its ATC button submits the same product form via
   form="…". Syncs price + availability with kk:variant:changed. Respects the
   enable setting (snippet omits the element entirely when disabled).

   Visibility is derived from the sentinel's live position (rect.bottom <= 0 ⇒ the
   buy box has fully scrolled out the TOP of the viewport ⇒ show; otherwise hide).
   We recompute on every scroll/resize (rAF-throttled) AND on IntersectionObserver
   callbacks. A pure IntersectionObserver toggle is NOT enough: on a fast flick or
   an anchor jump the sentinel passes through the viewport between IO samples, so
   `isIntersecting` never flips and the callback never fires — leaving the bar
   stuck visible over the H1 at scroll 0. The scroll listener guarantees the bar
   toggles OFF whenever the buy box is intersecting or below the viewport. */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.priceHost = this.querySelector('[data-sticky-price]');
    this.variantEl = this.querySelector('[data-sticky-variant]');
    // Live-parity markup (2026-08-10) renders TWO ATC containers (mobile in-row +
    // desktop bar-end, CSS-swapped at 768px) — drive BOTH; only one is ever visible.
    this.atcs = Array.from(this.querySelectorAll('[data-sticky-atc]'));
    this.atcTexts = Array.from(this.querySelectorAll('[data-sticky-atc-text]'));
    this.errorEl = this.querySelector('[data-sticky-error]');

    const sel = this.dataset.sentinel || '[data-buy-sentinel]';
    this.sentinel = document.querySelector(sel);

    if (this.sentinel) {
      this._update = this._update.bind(this);
      this._onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          this._update();
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(() => this._update(), { threshold: 0 });
        this._io.observe(this.sentinel);
      }
      // Initial state (bar stays hidden at the top where the buy box is on/below the fold).
      this._update();
    }

    if (window.KK && window.KK.pubsub) {
      this._unsub = window.KK.pubsub.subscribe('kk:variant:changed', (p) => {
        if (!p || p.sectionId !== this.sectionId) return;
        this.applyVariant(p.variant);
      });
    }

    // The Commslayer bubble often finishes mounting AFTER the bar is already
    // shown — re-lift it the moment the widget announces itself.
    this._onCwReady = () => this._syncBubble();
    window.addEventListener('chatwoot:ready', this._onCwReady);
  }

  disconnectedCallback() {
    if (this._io) this._io.disconnect();
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._unsub) this._unsub();
    if (this._onCwReady) window.removeEventListener('chatwoot:ready', this._onCwReady);
  }

  // Show ONLY once the buy box has fully scrolled out the TOP of the viewport
  // (its bottom edge is at/above the viewport top). While the buy box is on-screen
  // (intersecting) or still below the fold its bottom is > 0, so the bar hides —
  // this is what keeps it off the H1 at scroll 0 and after a fast scroll back up.
  _update() {
    if (!this.sentinel) return;
    const bottom = this.sentinel.getBoundingClientRect().bottom;
    this.setVisible(bottom <= 0);
  }

  setVisible(on) {
    this._visible = !!on;
    if (on) this.hidden = false;
    // allow the element to render before transitioning
    requestAnimationFrame(() => this.classList.toggle('is-visible', on));
    // Flag the root so the third-party chat bubble (appended to <body>, unreachable
    // by sibling selectors) can lift itself above the bar only while it is shown.
    document.documentElement.classList.toggle('kk-stickybar-on', this._visible);
    this._syncBubble();
  }

  // Lift the Commslayer bubble above the bar with an INLINE !important style.
  // The widget injects its stylesheet after ours, so a pure CSS class toggle can
  // lose the cascade tie in some load orders (owner saw the bubble back on the
  // HINZUFÜGEN button in incognito). Inline !important wins unconditionally.
  // Only the bubble elements — never .woot-widget-holder (the open chat window).
  _syncBubble() {
    const lift = this._visible && window.matchMedia('(max-width: 768px)').matches;
    // buy-path audit 2026-07-31: the lift used to be a hardcoded 92px against a 95px-tall
    // bar (3px intrusion). Measure the bar instead — the error row this cluster added makes
    // the bar taller on a failure, and a fixed number would put the bubble on the button.
    const h = Math.round(this.getBoundingClientRect().height) || 92;
    const els = document.querySelectorAll('.woot--bubble-holder, .woot-widget-bubble, .kk-chat-facade');
    for (let i = 0; i < els.length; i++) {
      if (lift) els[i].style.setProperty('bottom', (h + 8) + 'px', 'important');
      else els[i].style.removeProperty('bottom');
    }
  }

  applyVariant(variant) {
    const available = variant && variant.available;
    // buy-path audit 2026-07-31: this button is form-associated, so component-buy-buttons.js
    // owns its disabled/aria-busy state while an add is in flight. Never re-enable it here
    // mid-request — that would re-open the double-tap window this cluster just closed.
    // BuyButtons.applyVariant records the new availability and applies it on restore.
    this.atcs.forEach((btn) => {
      if (btn.getAttribute('aria-busy') !== 'true') btn.disabled = !available;
    });
    // A fresh, available variant makes a previous add error stale.
    if (available && this.errorEl) this.errorEl.hidden = true;
    // Keep the size/variant subtitle in sync with the picker (matches live's grey line).
    if (this.variantEl && variant && variant.options) {
      this.variantEl.textContent = variant.options.join(' / ');
    }
    this.atcTexts.forEach((t) => {
      t.textContent = available
        ? (t.getAttribute('data-add-text') || t.textContent)
        : (t.getAttribute('data-soldout-text') || t.textContent);
    });
    // Update the price from the fresh variant (money via KK.money). Mirrors the Liquid
    // render exactly — incl. the struck compare-at (owner 2026-08-10) — so a variant
    // change never changes the price row's shape.
    if (variant && this.priceHost && window.KK && window.KK.money) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      let html = '<span class="price' + (onSale ? ' price--sale' : '') + ' kk-sticky__price-val">';
      html += '<span class="price__amount">' + window.KK.money(variant.price) + '</span>';
      if (onSale) html += '<s class="price__compare">' + window.KK.money(variant.compare_at_price) + '</s>';
      html += '</span>';
      this.priceHost.innerHTML = html;
    }
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
